// Time Calculator logic — pure functions

export type TimeParts = { h: number; m: number; s: number };

const SEC_PER_DAY = 86400;

export function partsToSeconds(p: TimeParts): number {
  return ((p.h % 24) * 3600 + (p.m % 60) * 60 + (p.s % 60));
}

export function secondsToParts(total: number): TimeParts {
  const t = ((total % SEC_PER_DAY) + SEC_PER_DAY) % SEC_PER_DAY;
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return { h, m, s };
}

export function formatHMS(total: number): string {
  const sign = total < 0 ? "-" : "";
  const t = Math.abs(Math.trunc(total));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return `${sign}${h}:${pad(m)}:${pad(s)}`;
}

const pad = (n: number) => String(n).padStart(2, "0");

export function formatHM(total: number): string {
  const t = Math.max(0, Math.trunc(total));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  return `${h}:${pad(m)}`;
}

export function format12h(p: TimeParts): string {
  const h12 = ((p.h + 11) % 12) + 1;
  const ampm = p.h < 12 ? "AM" : "PM";
  return `${h12}:${pad(p.m)} ${ampm}`;
}

export function format24h(p: TimeParts): string {
  return `${pad(p.h)}:${pad(p.m)}`;
}

// Tab 1: duration between times (with optional dates)
export function durationBetween(
  start: TimeParts,
  end: TimeParts,
  startDate?: Date | null,
  endDate?: Date | null,
): { totalSeconds: number; h: number; m: number; s: number; days: number } {
  let dayDiff = 0;
  if (startDate && endDate) {
    const a = Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const b = Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    dayDiff = Math.round((b - a) / 86400000);
  }
  let diff = partsToSeconds(end) - partsToSeconds(start) + dayDiff * SEC_PER_DAY;
  if (!startDate && !endDate && diff < 0) diff += SEC_PER_DAY;
  const total = Math.max(0, diff);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return { totalSeconds: total, h, m, s, days: Math.floor(total / SEC_PER_DAY) };
}

// Tab 2: add/subtract time
export function shiftTime(
  start: TimeParts,
  delta: TimeParts,
  op: "add" | "sub",
): { result: TimeParts; dayOffset: number } {
  const startSec = partsToSeconds(start);
  const deltaSec = delta.h * 3600 + delta.m * 60 + delta.s;
  const total = op === "add" ? startSec + deltaSec : startSec - deltaSec;
  const dayOffset = Math.floor(total / SEC_PER_DAY);
  const wrapped = ((total % SEC_PER_DAY) + SEC_PER_DAY) % SEC_PER_DAY;
  return { result: secondsToParts(wrapped), dayOffset };
}

// Tab 3: timesheet sum
export function sumDurations(rows: TimeParts[]): number {
  return rows.reduce((sum, r) => sum + r.h * 3600 + r.m * 60 + r.s, 0);
}

export function calcWage(totalSeconds: number, ratePerHour: number): number {
  return (totalSeconds / 3600) * ratePerHour;
}

// Tab 4: unit converter
export type TimeUnit = "seconds" | "minutes" | "hours" | "days" | "weeks";
const TO_SECONDS: Record<TimeUnit, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
  weeks: 604800,
};

export function convertUnit(value: number, from: TimeUnit): Record<TimeUnit, number> {
  const seconds = value * TO_SECONDS[from];
  return {
    seconds,
    minutes: seconds / 60,
    hours: seconds / 3600,
    days: seconds / 86400,
    weeks: seconds / 604800,
  };
}

// Parse "HH:MM" or "HH:MM:SS" string
export function parseTimeString(input: string): TimeParts | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(":").map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return null;
  const [h = 0, m = 0, s = 0] = parts;
  return { h, m, s };
}
